import { useInfiniteQuery } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { InfiniteQueryConfig } from '@/src/lib/react-query/react-query';
import { BASE_PROJECT_PATH, generateRoutePath } from '@/src/routes';

import { INBOX_API_KEY } from '../../../api/config';
import { StatusFilterEnum, View } from '../../../types';

const PAGE_LIMIT = 50;

const url = `${PREFIX_URL}${BASE_PROJECT_PATH}/inbox`;

type InboxEntriesResponse = {
  data: any[];
};

const getFilterParam = (filter: StatusFilterEnum | View) => {
  if (!filter) {
    return {};
  }

  if (filter === StatusFilterEnum.Unread) {
    return {};
  }

  if (filter !== View.Trash && filter !== View.All && filter !== View.Files) {
    return {
      status: filter,
    };
  }

  if (filter === View.Trash) {
    return {
      trash: 1,
    };
  }
};

export const getInboxEntries = (projectId, params): Promise<InboxEntriesResponse> => {
  const baseUrl = generateRoutePath(url, { projectId });
  const myUrl = params.filter === StatusFilterEnum.Unread ? `${baseUrl}/unread` : baseUrl;
  const conditionalParams = getFilterParam(params.filter);
  const coditionalSearchParam = params.search ? { search: params.search } : {};

  return axios.get(myUrl, {
    params: {
      page: params.page,
      ...conditionalParams,
      ...coditionalSearchParam,
      limit: PAGE_LIMIT,
    },
  });
};

export const inboxEntriesKeys = {
  all: ['inboxEntries'] as const,
  lists: (params: { projectId: string; filter?: StatusFilterEnum | View; search?: string }) =>
    [INBOX_API_KEY, ...inboxEntriesKeys.all, { ...params, search: params.search || null }] as const,
};

type QueryFnType = typeof getInboxEntries;

type useInboxEntriesOptions = {
  projectId: string;
  filter: StatusFilterEnum | View;
  search: string;
  config?: InfiniteQueryConfig<QueryFnType>;
};

export const useInboxEntries = ({ projectId, config, ...otherParams }: useInboxEntriesOptions) => {
  return useInfiniteQuery({
    queryKey: [INBOX_API_KEY, ...inboxEntriesKeys.all, { projectId, ...otherParams }],
    queryFn: ({ pageParam = 1 }) => getInboxEntries(projectId, { page: pageParam, ...otherParams }),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.length < PAGE_LIMIT) {
        // If the last page's data is less than the limit, there are no more pages
        return undefined;
      }

      // Calculate the next page number based on the length of the current list data
      const nextPageNumber = allPages.length + 1;

      return nextPageNumber;
    },
    ...config,
  });
};
